import { useEffect } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { DianReadinessBanner } from "@/components/companies/DianReadinessBanner";
import { AppShell } from "@/components/layout/AppShell";
import { PosAdjustmentForm } from "@/components/pos-documents/PosAdjustmentForm";
import { RequireAuth } from "@/components/RequireAuth";
import { useActiveCompany } from "@/lib/company-context";
import type { PosAdjustmentDiscrepancyCode, PosAdjustmentNoteType } from "@/lib/api/pos-document-adjustments";

const DISCREPANCY = ["1", "2", "3", "4", "5"] as const;

type Search = {
  fromPos?: string;
  type?: PosAdjustmentNoteType;
  code?: PosAdjustmentDiscrepancyCode;
};

export const Route = createFileRoute("/companies/$companyId/pos/ajustes/new")({
  validateSearch: (search: Record<string, unknown>): Search => ({
    fromPos: typeof search.fromPos === "string" ? search.fromPos : undefined,
    type: search.type === "debit" || search.type === "credit" ? search.type : undefined,
    code: DISCREPANCY.includes(search.code as (typeof DISCREPANCY)[number])
      ? (search.code as PosAdjustmentDiscrepancyCode)
      : undefined,
  }),
  component: NewPosAdjustmentPage,
});

function NewPosAdjustmentPage() {
  return (
    <RequireAuth>
      <AppShell>
        <NewPosAdjustmentContent />
      </AppShell>
    </RequireAuth>
  );
}

function NewPosAdjustmentContent() {
  const { t } = useTranslation();
  const { companyId } = Route.useParams();
  const search = Route.useSearch();
  const { setActiveCompanyId } = useActiveCompany();

  useEffect(() => {
    setActiveCompanyId(companyId);
  }, [companyId, setActiveCompanyId]);

  return (
    <div className="space-y-6">
      <DianReadinessBanner companyId={companyId} documentKind="pos_ajuste" />
      <div>
        <Link to="/companies/$companyId/pos/ajustes" params={{ companyId }} className="text-sm text-muted-foreground">
          {t("pos.ajuste.new.back")}
        </Link>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight">{t("pos.ajuste.new.title")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("pos.ajuste.new.subtitle")}</p>
      </div>
      <PosAdjustmentForm
        companyId={companyId}
        fromPosId={search.fromPos}
        initialNoteType={search.type}
        initialDiscrepancyCode={search.code}
      />
    </div>
  );
}
